// Reference data: people are told, and a ban refuses a fresh F&O entry (`objects/ban.ts`).
import { type MarketCalendar, nextSessionDay } from './calendar.ts';
import type { Held } from './economic.ts';

/**
 * `BAN`: past 95% of the market-wide limit, so new F&O positions are refused and closing is allowed.
 * `RESULTS`: a board meeting whose purpose is results.
 * The last four come of an NSE circular, on the day they take effect: `ADJUSTMENT`, a corporate
 * action moves the stock's F&O contracts; `LOT_SIZE`, its lot is revised; `FNO_ENTRY`, its futures
 * and options begin; `FNO_EXIT`, its last contracts expire.
 */
export const STOCK_EVENT_KINDS = [
  'BAN',
  'RESULTS',
  'ADJUSTMENT',
  'LOT_SIZE',
  'FNO_ENTRY',
  'FNO_EXIT',
] as const;
export type StockEventKind = (typeof STOCK_EVENT_KINDS)[number];

/** A held stock whose board meets for results up to the next session: the open can gap on the news. */
export interface HeldIntoResults {
  underlying: string;
  day: string;
}

/**
 * Held into results, as `heldInto` is held into an economic event: up to the next session, a held
 * NSE name whose results are due. NSE gives the board meeting's day, not the hour the results land.
 */
export function heldIntoResults(
  events: readonly StockEvent[],
  calendar: MarketCalendar,
  today: string,
  held: readonly Held[],
): HeldIntoResults[] {
  const until = nextSessionDay(calendar, today);
  return held.flatMap((one) => {
    if (one.venue !== 'NFO') return [];
    const due = events
      .filter(
        (event) =>
          event.kind === 'RESULTS' &&
          event.symbol === one.underlying &&
          event.day >= today &&
          event.day <= until,
      )
      .map((event) => event.day)
      .sort()[0];
    return due === undefined ? [] : [{ underlying: one.underlying, day: due }];
  });
}

/** How far ahead results are read: NSE's one event-calendar answer covers it. */
export const RESULTS_AHEAD_DAYS = 14;

export interface StockEvent {
  day: string;
  /** NSE's symbol, which is the underlying an equity recommendation names. */
  symbol: string;
  kind: StockEventKind;
  /** NSE's own words: `Financial Results/Dividend`; empty for a ban. */
  said: string;
  /** The circular it came of, `NSE/FAOP/74527`; absent for what NSE's files say. */
  reference?: string;
}
